'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';

interface ExpoQRPreviewProps {
  expoUrl: string;
  title: string;
  className?: string;
  qrSize?: number;
}

const ExpoQRPreview = ({ 
  expoUrl, 
  title, 
  className = '', 
  qrSize = 200 
}: ExpoQRPreviewProps) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Generate QR code using qr-server.com API
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(expoUrl)}`;
    setQrCodeUrl(qrUrl);
  }, [expoUrl, qrSize]);

  const handleQRLoad = () => {
    setIsLoaded(true);
  };

  const handleQRError = () => {
    // Fallback to a simple text representation
    setIsLoaded(true);
  };

  return (
    <div className={`relative bg-zinc-900 rounded-lg overflow-hidden border border-zinc-700 ${className}`}>
      {/* Header */}
      <div className="p-4 bg-zinc-800 border-b border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-zinc-300 text-sm font-medium">Live Preview</span>
          </div>
          <div className="text-zinc-400 text-xs">
            Scan with Expo Go
          </div>
        </div>
        <h3 className="text-white font-semibold mt-2">{title}</h3>
      </div>

      {/* QR Code Container */}
      <div className="p-6 flex flex-col items-center space-y-4">
        <div className="relative">
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          {qrCodeUrl && (
            <Image
              src={qrCodeUrl}
              alt={`QR Code for ${title}`}
              className={`w-48 h-48 border-2 border-zinc-700 rounded-lg bg-white p-2 transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={handleQRLoad}
              onError={handleQRError}
            />
          )}
        </div>

        {/* Instructions */}
        <div className="text-center space-y-2">
          <div className="text-zinc-300 text-sm">
            📱 Download <span className="text-blue-400 font-semibold">Expo Go</span> app
          </div>
          <div className="text-zinc-400 text-xs">
            Scan this QR code to preview the app on your device
          </div>
        </div>

        {/* App Store Links */}
        <div className="flex space-x-3">
          <a
            href="https://apps.apple.com/us/app/expo-go/id982107779"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-3 py-2 bg-black hover:bg-gray-800 text-white text-xs rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <span>iOS</span>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=host.exp.exponent"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-3 py-2 bg-black hover:bg-gray-800 text-white text-xs rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            <span>Android</span>
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 bg-zinc-800 border-t border-zinc-700">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <div className="flex items-center space-x-2">
            <span>⚡</span>
            <span>Real device preview</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>Powered by</span>
            <span className="text-blue-400 font-semibold">Expo Go</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpoQRPreview;
